import type { components } from '@feedfm/clips-services-api';
import type { ReactNode } from 'react';
import type { ClipTransportBarProps } from './ClipTransportBar';

/**
 * A single track with its curated clip + zero or more custom clips.
 * Consumers shape their fetched data into this structure before passing
 * it to <TrackClipTable>.
 */
export interface TrackWithClips {
  trackId: number;
  trackTitle: string;
  artist: string;
  album: string;
  artworkThumbnailUrl?: string;
  artworkPreSignedUrl?: string;
  /** Full-track audio URL; enables real preview playback in the clip editor. */
  audioUrl?: string;
  explicitness?: components['schemas']['Explicitness'];
  /** Display name of the language of performance. Undefined renders an empty cell. */
  language?: string;
  /** Popularity score, roughly 0–100 (prototype: dummy value). */
  popularity?: number;
  /** Full source-track duration in milliseconds. */
  trackDurationMs: number;
  /** When the track was added to the catalog, as epoch ms. */
  dateAddedMs?: number;
  /** The curated clip representing this track. */
  curatedClip: ClipSummary;
  /** True when a real curator clip backs the primary clip; false => synthesized default. */
  hasCuratedClip?: boolean;
  /** Custom clips defined on this track. May be empty. */
  customClips: CustomClipSummary[];
}

export interface ClipSummary {
  clipId: number;
  /** Start offset within the source track, in milliseconds. */
  startMs: number;
  /** End offset within the source track, in milliseconds. */
  endMs: number;
}

export interface CustomClipSummary extends ClipSummary {
  name: string;
  /** When the custom clip was created, as epoch ms. */
  dateAddedMs?: number;
}

/**
 * Draft passed to onCreate / onUpdate callbacks. The consumer is
 * responsible for persisting it.
 */
export interface ClipDraft {
  name: string;
  startMs: number;
  endMs: number;
}

export const DEFAULT_COLUMNS = [
  'thumbnail',
  'titleArtist',
  'album',
  'explicitness',
  'dateAdded',
  'language',
  'popularity',
  'more',
] as const;

export type ColumnKey = (typeof DEFAULT_COLUMNS)[number];

export interface TrackClipTableProps {
  tracks: TrackWithClips[];
  onCreateCustomClip: (trackId: number, draft: ClipDraft) => Promise<void>;
  onUpdateCustomClip: (clipId: number, patch: ClipDraft) => Promise<void>;
  onDeleteCustomClip: (clipId: number) => Promise<void>;
  /** Optional: hide / reorder columns. Defaults to DEFAULT_COLUMNS. */
  columns?: ColumnKey[];
  /** Rendered when tracks is empty. */
  emptyState?: ReactNode;
  isLoading?: boolean;
  /** Called when a parent row's play button is clicked. */
  onPlayCuratedClip?: (trackId: number) => void;
  /** Called when a child row's play button is clicked. */
  onPlayCustomClip?: (clipId: number) => void;
  /** Used to render the "playing" state on a row's play button. */
  isClipPlaying?: (clipId: number) => boolean;
  /**
   * When provided, the Title/Artist header becomes a clickable sort toggle that
   * calls this on each click. The consumer owns the click cycle.
   */
  onTitleArtistSort?: () => void;
  /** Which half of the Title/Artist header is the active sort, if any. */
  titleArtistSortField?: 'title' | 'artist' | null;
  /**
   * Column keys that render a standard asc/desc sort toggle in their header.
   * Clicking one calls onSort with that key.
   */
  sortableColumns?: ColumnKey[];
  /** The standard column whose header shows the active sort indicator. */
  activeSortColumn?: ColumnKey | null;
  /** Direction of the active sort, shared by Title/Artist and standard columns. */
  sortOrder?: 'asc' | 'desc';
  /** Called with the clicked standard sortable column key. */
  onSort?: (column: ColumnKey) => void;
  /**
   * Called when audible preview playback starts in the clip editor.
   * Consumers typically pause the global player here.
   */
  onEditorPreviewPlay?: () => void;
  /** When provided, each curated row renders a "Find Similar" action. */
  onFindSimilar?: (track: TrackWithClips) => void;
  /**
   * Playback transport wired to the shared global player state. Forwarded to the
   * Clip Options modal so it shows a synced play/seek/volume bar.
   */
  transport?: ClipTransportBarProps;
}
